import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Gauge, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { Spinner } from "@heroui/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  useEnergyPerformanceData,
  EPCLabel,
  InvestmentByCategory,
} from "../hooks/useEnergyPerformanceData";
import {
  EnergyHeroKpiCards,
  InvestmentByCategoryChart,
  EnergyActionTabs,
  filterByActionTab,
  getStoredActionTab,
} from "../components";
import type { InvestmentMode } from "../components";
import type { ActionTab } from "../components";
import { EnergyPerformanceUniversalTable } from "shared/components/renovation/components/EnergyPerformanceUniversalTable";
import {
  UniversalTableSearch,
  UniversalFilterSection,
} from "shared/components/UniversalTable";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// EPC color mapping for charts (hex values)
const EPC_COLORS: Record<string, string> = {
  "A++": "#22C55E",
  "A+": "#22C55E",
  A: "#22C55E",
  B: "#5B7304",
  C: "#F59E0B",
  D: "#F97316",
  E: "#EA8162",
  F: "#EA8162",
  G: "#EA8162",
  Onbekend: "#838C89",
  "Niet van toepassing": "#838C89",
  "Gemeenschappelijke delen": "#31403B",
  Ontbrekend: "#D1D5DB",
};

const EnergieprestatiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    epcData,
    metrics,
    isLoading,
    filterOptions,
    refresh,
    refreshSingleEPC,
    deleteEPC,
    avgEPCLabel,
  } = useEnergyPerformanceData();

  // ─── Filter states ──────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    epcLabel: [],
    unitType: [],
    trend: [],
    worksDone: [],
    epcAction: [],
  });
  const [activeMetricFilter, setActiveMetricFilter] = useState<string | null>(null);

  // ─── Investment toggle (planned / actueel) — page-level state ──────────────
  const [investmentMode, setInvestmentMode] = useState<InvestmentMode>("actueel");

  // ─── Action tabs — persisted per session ───────────────────────────────────
  const [activeActionTab, setActiveActionTab] = useState<ActionTab>(
    getStoredActionTab,
  );

  // Initialize sticky top as 0 for this page
  useEffect(() => {
    document.documentElement.style.setProperty("--sticky-top", "0px");
  }, []);

  // Read URL params on mount for deep linking
  useEffect(() => {
    const epcLabelParam = searchParams.get("epcLabel");
    if (epcLabelParam) {
      setActiveFilters((prev) => ({
        ...prev,
        epcLabel: [epcLabelParam],
      }));
      setSearchParams({}, { replace: true });
    }
  }, []);

  // ─── Filter sections ────────────────────────────────────────────────────────
  const filterSections: UniversalFilterSection[] = useMemo(() => {
    const sections: UniversalFilterSection[] = [];

    if (filterOptions.epcLabels.length > 0) {
      sections.push({
        key: "epcLabel",
        label: "EPC Label",
        options: filterOptions.epcLabels.map((label) => ({
          key: label,
          label: label,
        })),
        color: "moss",
        multiSelect: true,
      });
    }

    if (filterOptions.unitTypes.length > 0) {
      sections.push({
        key: "unitType",
        label: "Type",
        options: filterOptions.unitTypes.map((type) => ({
          key: type,
          label: type,
        })),
        color: "brick",
        multiSelect: true,
      });
    }

    sections.push({
      key: "trend",
      label: "EPC Trend",
      options: [
        { key: "improved", label: "Verbeterd" },
        { key: "declined", label: "Verslechterd" },
        { key: "unchanged", label: "Onveranderd" },
      ],
      color: "neutral",
      multiSelect: true,
    });

    sections.push({
      key: "worksDone",
      label: "Renovatiewerken",
      options: [
        { key: "done", label: "Werken uitgevoerd" },
        { key: "none", label: "Geen werken" },
      ],
      color: "moss",
      multiSelect: true,
    });

    sections.push({
      key: "epcAction",
      label: "EPC actie",
      options: [
        { key: "needed", label: "Nieuwe EPC nodig" },
        { key: "energetic", label: "Energetisch nodig" },
      ],
      color: "brick",
      multiSelect: true,
    });

    return sections;
  }, [filterOptions]);

  // ─── Filter handlers ────────────────────────────────────────────────────────
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters((prev) => ({ ...prev, [key]: values }));
    if (values.length > 0) {
      setActiveMetricFilter(null);
    }
  }, []);

  const handleMetricFilter = useCallback(
    (filterType: string) => {
      setActiveFilters({
        epcLabel: [],
        unitType: [],
        trend: [],
        worksDone: [],
        epcAction: [],
      });
      setSearchTerm("");
      setActiveMetricFilter((prev) =>
        prev === filterType ? null : filterType,
      );
    },
    [],
  );

  // ─── Filtered data (metric filter + text/facet filters) ────────────────────
  const filteredData = useMemo(() => {
    let filtered = [...epcData];

    if (activeMetricFilter) {
      switch (activeMetricFilter) {
        case "withEPC":
          filtered = filtered.filter(
            (unit) =>
              unit.currentEPC && unit.currentEPC.label !== EPCLabel.ONTBREKEND,
          );
          break;
        case "gemeenschappelijkeDelen":
          filtered = filtered.filter(
            (unit) => unit.unitType === "Gemeenschappelijke delen",
          );
          break;
        case "improved":
          filtered = filtered.filter(
            (unit) => unit.epcEvolution === "improved",
          );
          break;
        case "declined":
          filtered = filtered.filter(
            (unit) => unit.epcEvolution === "declined",
          );
          break;
        case "unchanged":
          filtered = filtered.filter(
            (unit) => unit.epcEvolution === "unchanged",
          );
          break;
        case "missingEPC":
          filtered = filtered.filter(
            (unit) =>
              !unit.currentEPC || unit.currentEPC.label === EPCLabel.ONTBREKEND,
          );
          break;
        case "worksDone":
          filtered = filtered.filter(
            (unit) => (unit.projectPartGroups?.length ?? 0) > 0,
          );
          break;
        case "needsNewEpc":
          filtered = filtered.filter((unit) => unit.epcRefreshSuggested);
          break;
        case "needsEnergeticEpc":
          filtered = filtered.filter(
            (unit) =>
              unit.epcRefreshSuggested && unit.epcRefreshReason === "ENERGETIC",
          );
          break;
        case "invested":
          // Show units that have any investment > 0
          filtered = filtered.filter(
            (unit) => unit.investment.plannedTotal > 0 || unit.investment.actualTotal > 0,
          );
          break;
      }
    } else {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (unit) =>
            unit.unitLocation.toLowerCase().includes(searchLower) ||
            unit.buildingLocation.toLowerCase().includes(searchLower),
        );
      }

      const epcLabelFilter = activeFilters.epcLabel || [];
      if (epcLabelFilter.length > 0) {
        filtered = filtered.filter(
          (unit) =>
            unit.currentEPC && epcLabelFilter.includes(unit.currentEPC.label),
        );
      }

      const unitTypeFilter = activeFilters.unitType || [];
      if (unitTypeFilter.length > 0) {
        filtered = filtered.filter((unit) =>
          unitTypeFilter.includes(unit.unitType),
        );
      }

      const trendFilter = activeFilters.trend || [];
      if (trendFilter.length > 0) {
        filtered = filtered.filter((unit) => {
          if (!unit.epcEvolution) return false;
          return trendFilter.includes(unit.epcEvolution);
        });
      }

      const worksDoneFilter = activeFilters.worksDone || [];
      if (worksDoneFilter.length > 0) {
        filtered = filtered.filter((unit) => {
          const hasWorks = (unit.projectPartGroups?.length ?? 0) > 0;
          if (worksDoneFilter.includes("done") && hasWorks) return true;
          if (worksDoneFilter.includes("none") && !hasWorks) return true;
          return false;
        });
      }

      const epcActionFilter = activeFilters.epcAction || [];
      if (epcActionFilter.length > 0) {
        filtered = filtered.filter((unit) => {
          if (epcActionFilter.includes("needed") && unit.epcRefreshSuggested) {
            return true;
          }
          if (
            epcActionFilter.includes("energetic") &&
            unit.epcRefreshSuggested &&
            unit.epcRefreshReason === "ENERGETIC"
          ) {
            return true;
          }
          return false;
        });
      }
    }

    return filtered;
  }, [epcData, activeMetricFilter, searchTerm, activeFilters]);

  // Action tab filter applied on top of filteredData
  const tableData = useMemo(
    () => filterByActionTab(filteredData, activeActionTab),
    [filteredData, activeActionTab],
  );

  // ─── Category chart click — filter table to show units with renovation works ──
  const handleCategoryClick = useCallback(
    (_category: keyof InvestmentByCategory) => {
      setActiveFilters((prev) => ({
        ...prev,
        worksDone: ["done"],
      }));
      setActiveMetricFilter(null);
      setSearchTerm("");
    },
    [],
  );

  // ─── EPC handlers ───────────────────────────────────────────────────────────
  const handleRefreshSingleEPC = useCallback(
    async (fileLogId: number) => {
      await refreshSingleEPC(fileLogId);
    },
    [refreshSingleEPC],
  );

  const handleDeleteEPC = useCallback(
    async (fileLogId: number) => {
      return await deleteEPC(fileLogId);
    },
    [deleteEPC],
  );

  if (isLoading && epcData.length === 0) {
    return (
      <>
        <HelmetProvider>
          <Helmet>
            <title>Energieprestaties | Renovatie | Yally</title>
          </Helmet>
        </HelmetProvider>
        <div className="bg-background min-h-screen">
          <div className="container mx-auto px-4 py-6 lg:px-6">
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Energieprestaties | Renovatie | Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="bg-background min-h-screen">
        <div className="container mx-auto px-4 py-6 lg:px-6">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-moss-primary/10 rounded-lg p-2">
                <Gauge className="text-moss-primary h-6 w-6" weight="duotone" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Energieprestaties
                </h1>
                <p className="text-neutral-secondary-mid text-sm">
                  Overzicht van EPC labels en energetische renovaties
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={isLoading}
                className="gap-2"
              >
                <ArrowsClockwise
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  weight="bold"
                />
                Vernieuwen
              </Button>
            </div>
          </div>

          {/* Hero KPI cards — replaces old overview group */}
          <EnergyHeroKpiCards
            epcData={epcData}
            metrics={metrics}
            avgEPCLabel={avgEPCLabel}
            activeFilter={activeMetricFilter}
            investmentMode={investmentMode}
            onInvestmentModeChange={setInvestmentMode}
            onFilterClick={handleMetricFilter}
          />

          {/* Charts row */}
          {metrics && (
            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* LEFT: EPC Label Verdeling bar chart */}
              <div className="border-neutral-primary/10 rounded-lg border bg-white shadow-sm">
                <div className="border-neutral-primary/10 border-b p-4">
                  <h3 className="text-neutral-primary flex items-center gap-2 text-base font-semibold">
                    <Gauge
                      className="text-moss-primary h-5 w-5"
                      weight="duotone"
                    />
                    EPC Label Verdeling
                  </h3>
                  <p className="text-neutral-secondary-mid text-sm">
                    Aantal units per EPC label
                  </p>
                </div>
                <div className="p-4">
                  <div className="h-[280px]">
                    <Bar
                      data={{
                        labels: Object.keys(
                          metrics.epcDistribution || {},
                        ).filter(
                          (label) => label !== "Gemeenschappelijke delen",
                        ),
                        datasets: [
                          {
                            label: "Aantal units",
                            data: Object.entries(metrics.epcDistribution || {})
                              .filter(
                                ([label]) =>
                                  label !== "Gemeenschappelijke delen",
                              )
                              .map(([, count]) => count),
                            backgroundColor: Object.keys(
                              metrics.epcDistribution || {},
                            )
                              .filter(
                                (label) => label !== "Gemeenschappelijke delen",
                              )
                              .map((label) => EPC_COLORS[label] || "#838C89"),
                            borderRadius: 4,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        indexAxis: "y" as const,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            callbacks: {
                              label: (context: any) => {
                                const percentage = Math.round(
                                  (context.raw / metrics.totalUnits) * 100,
                                );
                                return `${context.raw} units (${percentage}%)`;
                              },
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: {
                              color: "rgba(0, 0, 0, 0.05)",
                            },
                          },
                          y: {
                            grid: {
                              display: false,
                            },
                          },
                        },
                        onClick: (_event: any, elements: any) => {
                          if (elements.length > 0) {
                            const index = elements[0].index;
                            const labels = Object.keys(
                              metrics.epcDistribution || {},
                            ).filter(
                              (label) => label !== "Gemeenschappelijke delen",
                            );
                            const clickedLabel = labels[index];
                            setActiveMetricFilter(null);
                            setActiveFilters({
                              epcLabel: [clickedLabel],
                              unitType: [],
                              trend: [],
                              worksDone: [],
                              epcAction: [],
                            });
                            setSearchTerm("");
                          }
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT: Investering per Labelcategorie */}
              <InvestmentByCategoryChart
                metrics={metrics}
                investmentMode={investmentMode}
                onCategoryClick={handleCategoryClick}
              />
            </div>
          )}

          {/* Filters using UniversalTableSearch */}
          <UniversalTableSearch
            rows={filteredData}
            searchValue={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              if (value) {
                setActiveMetricFilter(null);
              }
            }}
            searchPlaceholder="Zoek op locatie..."
            filterSections={filterSections}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            isLoading={isLoading}
            showSpacer={false}
          />

          {/* Action tabs */}
          <EnergyActionTabs
            data={filteredData}
            activeTab={activeActionTab}
            onTabChange={setActiveActionTab}
          />

          {/* Results count */}
          <p className="text-neutral-secondary-mid mb-4 text-sm">
            {tableData.length} van {epcData.length} units weergegeven
          </p>

          {/* Table */}
          <div className="border-neutral-primary/10 mb-24 rounded-lg border bg-white shadow-sm">
            <EnergyPerformanceUniversalTable
              data={tableData}
              investmentMode={investmentMode}
              onRefreshSingleEPC={handleRefreshSingleEPC}
              onDeleteEPC={handleDeleteEPC}
              onRefresh={refresh}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EnergieprestatiesPage;
